import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CloudUpload,
  Preview,
  CheckCircle,
  Error,
  Warning,
  Delete,
  Edit,
  Download
} from '@mui/icons-material';
import Papa from 'papaparse';

const CSVImport = () => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showMapping, setShowMapping] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef();

  const defaultMapping = {
    name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    location: '',
    notes: ''
  };

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    setErrors([]);
    setImportResults(null);

    Papa.parse(uploadedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setErrors(results.errors.map(err => `Row ${err.row}: ${err.message}`));
          return;
        }

        setCsvData(results.data);
        setHeaders(Object.keys(results.data[0] || {}));
        setPreviewData(results.data.slice(0, 5)); // Show first 5 rows
        
        // Auto-map common column names
        const autoMapping = {};
        headers.forEach(header => {
          const lowerHeader = header.toLowerCase();
          if (lowerHeader.includes('name') || lowerHeader.includes('full')) {
            autoMapping.name = header;
          } else if (lowerHeader.includes('email') || lowerHeader.includes('mail')) {
            autoMapping.email = header;
          } else if (lowerHeader.includes('phone') || lowerHeader.includes('tel')) {
            autoMapping.phone = header;
          } else if (lowerHeader.includes('company') || lowerHeader.includes('org')) {
            autoMapping.company = header;
          } else if (lowerHeader.includes('title') || lowerHeader.includes('position') || lowerHeader.includes('job')) {
            autoMapping.title = header;
          } else if (lowerHeader.includes('location') || lowerHeader.includes('city') || lowerHeader.includes('address')) {
            autoMapping.location = header;
          } else if (lowerHeader.includes('note') || lowerHeader.includes('comment')) {
            autoMapping.notes = header;
          }
        });
        setMapping(autoMapping);
      },
      error: (error) => {
        setErrors([`File parsing error: ${error.message}`]);
      }
    });
  };

  const handleImport = async () => {
    if (!csvData.length) return;

    setImporting(true);
    setImportResults(null);

    try {
      const response = await fetch('https://social-media-backend.andrewrkloc.workers.dev/api/leads/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          csvData,
          mapping
        })
      });

      const result = await response.json();

      if (result.success) {
        setImportResults(result.data);
        setShowPreview(false);
        setShowMapping(false);
      } else {
        setErrors([result.error]);
      }
    } catch (error) {
      setErrors([`Import failed: ${error.message}`]);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      ['name', 'email', 'phone', 'company', 'title', 'location', 'notes'],
      ['John Doe', 'john@example.com', '+1-555-0123', 'Tech Corp', 'CEO', 'San Francisco, CA', 'Interested in our platform'],
      ['Jane Smith', 'jane@example.com', '+1-555-0124', 'Startup Inc', 'CTO', 'New York, NY', 'Looking for solutions']
    ];

    const csv = Papa.unparse(template);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetImport = () => {
    setFile(null);
    setCsvData([]);
    setHeaders([]);
    setMapping(defaultMapping);
    setPreviewData([]);
    setImportResults(null);
    setErrors([]);
    setShowPreview(false);
    setShowMapping(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Import Leads from CSV
      </Typography>

      {/* File Upload Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          
          {!file ? (
            <Box>
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ mb: 2 }}
              >
                Choose CSV File
              </Button>
              <Typography variant="body2" color="text.secondary">
                or drag and drop a CSV file here
              </Typography>
              <Button
                variant="text"
                startIcon={<Download />}
                onClick={downloadTemplate}
                sx={{ mt: 2 }}
              >
                Download Template
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {csvData.length} rows detected
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Preview />}
                  onClick={() => setShowPreview(true)}
                  sx={{ mr: 1 }}
                >
                  Preview Data
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => setShowMapping(true)}
                  sx={{ mr: 1 }}
                >
                  Map Columns
                </Button>
                <Button
                  variant="contained"
                  onClick={handleImport}
                  disabled={importing || csvData.length === 0}
                  startIcon={importing ? <LinearProgress /> : <CloudUpload />}
                >
                  {importing ? 'Importing...' : 'Import Leads'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Errors */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6">Import Errors:</Typography>
          {errors.map((error, index) => (
            <Typography key={index} variant="body2">
              • {error}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Import Results */}
      {importResults && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Import Results
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="success.main">
                    {importResults.imported}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Successfully Imported
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="error.main">
                    {importResults.failed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Failed to Import
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="primary.main">
                    {importResults.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Rows
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {importResults.errors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Failed Rows:
              </Typography>
              {importResults.errors.slice(0, 10).map((error, index) => (
                <Typography key={index} variant="body2" color="error.main">
                  • {error}
                </Typography>
              ))}
              {importResults.errors.length > 10 && (
                <Typography variant="body2" color="text.secondary">
                  ... and {importResults.errors.length - 10} more errors
                </Typography>
              )}
            </Box>
          )}
          
          <Button
            variant="outlined"
            onClick={resetImport}
            sx={{ mt: 2 }}
          >
            Import Another File
          </Button>
        </Paper>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Data Preview (First 5 rows)
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableCell key={header}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.map((row, index) => (
                  <TableRow key={index}>
                    {headers.map((header) => (
                      <TableCell key={header}>
                        {row[header] || ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Column Mapping Dialog */}
      <Dialog
        open={showMapping}
        onClose={() => setShowMapping(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Map CSV Columns to Lead Fields
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which CSV column corresponds to each lead field
          </Typography>
          
          <Grid container spacing={2}>
            {Object.keys(defaultMapping).map((field) => (
              <Grid item xs={12} md={6} key={field}>
                <FormControl fullWidth>
                  <InputLabel>{field.charAt(0).toUpperCase() + field.slice(1)}</InputLabel>
                  <Select
                    value={mapping[field] || ''}
                    onChange={(e) => setMapping({ ...mapping, [field]: e.target.value })}
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {headers.map((header) => (
                      <MenuItem key={header} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Mapping Summary:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(mapping).map(([field, column]) => (
                column && (
                  <Chip
                    key={field}
                    label={`${field}: ${column}`}
                    color="primary"
                    variant="outlined"
                  />
                )
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMapping(false)}>Cancel</Button>
          <Button 
            onClick={() => setShowMapping(false)}
            variant="contained"
          >
            Save Mapping
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CSVImport;

